/**
 * TopBar — Header bar with search trigger, notifications, user menu.
 * v4.8 Spec: "Global command bar (Cmd+K) from any screen."
 * Shows page title on mobile, search + actions on desktop.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/services/useAuth';
import { useAuthContext } from '@/contexts/AuthContext';
import { NavIcon } from './NavIcon';

export function TopBar(): React.ReactElement {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { profile } = useAuthContext();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Cmd+K shortcut for command bar
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // TODO: Open command bar — Phase 2
        console.log('Command bar triggered');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSignOut = useCallback(async (): Promise<void> => {
    await signOut();
    navigate('/auth/login', { replace: true });
  }, [signOut, navigate]);

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between h-14 px-4 md:px-6 bg-surface-page/80 dark:bg-dark-page/80 backdrop-blur-md border-b border-border-light dark:border-border-dark">
      {/* Left: Page title on mobile, search trigger on desktop */}
      <div className="flex items-center gap-3">
        {/* Search trigger — Cmd+K */}
        <button
          type="button"
          onClick={() => { /* TODO: command bar */ }}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-button bg-white/60 dark:bg-white/5 border border-border-light dark:border-border-dark text-text-secondary dark:text-text-dark-secondary hover:border-gold/40 transition-all duration-fast text-sm"
        >
          <NavIcon name="search" className="w-4 h-4" />
          <span>Search...</span>
          <kbd className="ml-4 px-1.5 py-0.5 rounded text-[10px] font-mono bg-black/5 dark:bg-white/10 text-text-secondary dark:text-text-dark-secondary">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Notification bell */}
        <button
          type="button"
          onClick={() => navigate('/comms/notifications')}
          className="touch-target text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors relative"
          aria-label="Notifications"
        >
          <NavIcon name="bell" className="w-5 h-5" />
          {/* Unread badge — controlled by Supabase Realtime in production */}
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowUserMenu((v) => !v)}
            className="touch-target"
            aria-label="User menu"
          >
            <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-xs font-semibold text-gold">
                {profile?.full_name.charAt(0).toUpperCase() ?? '?'}
              </span>
            </div>
          </button>

          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
                onKeyDown={(e) => { if (e.key === 'Escape') setShowUserMenu(false); }}
                role="button"
                tabIndex={-1}
                aria-label="Close menu"
              />
              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 w-56 glass-card p-2 z-50 animate-spring">
                {profile && (
                  <div className="px-3 py-2 mb-1">
                    <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary truncate">
                      {profile.full_name}
                    </p>
                    <p className="text-xs text-text-secondary dark:text-text-dark-secondary truncate">
                      {profile.email}
                    </p>
                  </div>
                )}
                <div className="h-px bg-border-light dark:bg-border-dark my-1" />
                <button
                  type="button"
                  onClick={() => { navigate('/system/admin'); setShowUserMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-button text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left"
                >
                  <NavIcon name="settings" className="w-4 h-4" />
                  Settings
                </button>
                <button
                  type="button"
                  onClick={() => void handleSignOut()}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-button text-sm text-danger-light dark:text-danger-dark hover:bg-danger-light/5 dark:hover:bg-danger-dark/5 transition-colors text-left"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
